import React from 'react';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '@heroui/react';
import { Widget } from '@heroui-pro/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';

const PLACEHOLDER_ROW_COUNT = 5;

/**
 * Mirrors `ActiveDraftsView`'s row layout. The trailing action group
 * is two side-by-side pill placeholders to match the Edit + Delete
 * pair (vs the single pill the pending skeleton uses).
 */
export const ActiveDraftsSkeleton: React.FC = () => {
    const { t } = useTranslation('pages');

    const copy = React.useMemo(
        () => ({
            title: t('purchases.widgets.activeDrafts.title'),
        }),
        [t],
    );

    return (
        <section
            aria-busy
            aria-labelledby="active-drafts-widget-title"
            {...getNodeTestIdProp('active-drafts-section')}
        >
            <Widget
                className="flex flex-col gap-0 overflow-hidden p-0"
                {...getNodeTestIdProp('active-drafts-widget')}
            >
                <Widget.Header
                    className="flex items-center justify-between gap-3 border-b border-default-200 px-6 py-5"
                    {...getNodeTestIdProp('active-drafts-widget-header')}
                >
                    <div
                        className="flex items-center gap-3"
                        {...getNodeTestIdProp('active-drafts-header-titles')}
                    >
                        <Skeleton
                            className="size-8 shrink-0 rounded-lg"
                            {...getNodeTestIdProp(
                                'active-drafts-icon-skeleton',
                            )}
                        />
                        <div
                            className="flex flex-col gap-1.5"
                            {...getNodeTestIdProp('active-drafts-title-stack')}
                        >
                            <Widget.Title
                                className="text-base font-semibold leading-none text-foreground"
                                {...getNodeTestIdProp(
                                    'active-drafts-widget-title',
                                )}
                            >
                                {copy.title}
                            </Widget.Title>
                            <Widget.Description
                                {...getNodeTestIdProp(
                                    'active-drafts-widget-subtitle',
                                )}
                            >
                                <Skeleton
                                    className="h-3 w-24 rounded-md"
                                    {...getNodeTestIdProp(
                                        'active-drafts-widget-subtitle-skeleton',
                                    )}
                                />
                            </Widget.Description>
                        </div>
                    </div>
                </Widget.Header>
                <ul
                    className="divide-y divide-default-200"
                    {...getNodeTestIdProp('active-drafts-grid-skeleton')}
                >
                    {Array.from({ length: PLACEHOLDER_ROW_COUNT }).map(
                        (_, index) => (
                            <li
                                key={index}
                                className="flex items-center gap-4 px-6 py-3.5"
                                {...getNodeTestIdProp(
                                    `active-drafts-row-placeholder-${index}`,
                                )}
                            >
                                <Skeleton
                                    className="h-3 w-24 rounded-md"
                                    {...getNodeTestIdProp(
                                        `active-drafts-row-number-placeholder-${index}`,
                                    )}
                                />
                                <Skeleton
                                    className="h-3 flex-1 rounded-md"
                                    {...getNodeTestIdProp(
                                        `active-drafts-row-supplier-placeholder-${index}`,
                                    )}
                                />
                                <Skeleton
                                    className="h-3 w-28 rounded-md"
                                    {...getNodeTestIdProp(
                                        `active-drafts-row-date-placeholder-${index}`,
                                    )}
                                />
                                <Skeleton
                                    className="h-3 w-10 rounded-md"
                                    {...getNodeTestIdProp(
                                        `active-drafts-row-count-placeholder-${index}`,
                                    )}
                                />
                                <Skeleton
                                    className="h-3.5 w-20 rounded-md"
                                    {...getNodeTestIdProp(
                                        `active-drafts-row-total-placeholder-${index}`,
                                    )}
                                />
                                <div
                                    className="flex items-center gap-1"
                                    {...getNodeTestIdProp(
                                        `active-drafts-row-actions-placeholder-${index}`,
                                    )}
                                >
                                    <Skeleton
                                        className="h-8 w-20 rounded-lg"
                                        {...getNodeTestIdProp(
                                            `active-drafts-row-edit-placeholder-${index}`,
                                        )}
                                    />
                                    <Skeleton
                                        className="size-8 rounded-lg"
                                        {...getNodeTestIdProp(
                                            `active-drafts-row-delete-placeholder-${index}`,
                                        )}
                                    />
                                </div>
                            </li>
                        ),
                    )}
                </ul>
            </Widget>
        </section>
    );
};
